import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import {
  accessTokenIndicatesInactiveAccount,
  decodeStaffRoleFromToken,
  decodeUserFromToken,
  getContributorAccessToken,
  getContributorRefreshToken,
  getStaffAccessToken,
  getStaffRefreshToken,
  INACTIVE_ACCOUNT_MESSAGE,
  loginPortalPassword,
  loginStaffPassword,
  logoutContributorRemote,
  logoutStaffRemote,
  setContributorTokens,
  setStaffTokens,
  syncPortalRbacFromServer,
  syncStaffRbacFromServer,
  type StaffRole,
} from "@/lib/authApi";
import {
  type AppLexiconRole,
  type JainLexiconRbacState,
  getStoredPortalRbac,
  getStoredStaffRbac,
  rbacHasPermission,
  rbacHasRole,
  rbacIsContributor,
  rbacIsLexiconAdmin,
  rbacIsScholar,
  setStoredPortalRbac,
  setStoredStaffRbac,
} from "@jl/lib/jainLexiconRbac";

type User = { id: string; email: string | null };
type Session = { user: User };

interface AuthContextType {
  /** Contributor portal session (public site). */
  contributorSession: Session | null;
  contributorUser: User | null;
  isContributorAuthenticated: boolean;
  /** RBAC for portal JWT (group, permissions). */
  portalRbac: JainLexiconRbacState | null;
  /** Staff (admin/scholar) session — separate storage; used on /admin. */
  staffSession: Session | null;
  staffUser: User | null;
  staffRole: StaffRole | null;
  isStaffAuthenticated: boolean;
  /** RBAC for staff JWT. */
  staffRbac: JainLexiconRbacState | null;
  canAccessStaffDashboard: boolean;

  hasPortalPermission: (permissionId: number) => boolean;
  hasStaffPermission: (permissionId: number) => boolean;
  hasPortalRole: (role: AppLexiconRole) => boolean;
  hasStaffRole: (role: AppLexiconRole) => boolean;
  isLexiconPortalAdmin: () => boolean;
  isLexiconPortalScholar: () => boolean;
  isLexiconPortalContributor: () => boolean;
  isLexiconStaffAdmin: () => boolean;
  isLexiconStaffScholar: () => boolean;

  /** @deprecated Prefer contributorUser */
  user: User | null;
  isLoading: boolean;
  /** @deprecated Prefer JWT-derived flags: `staffRole`, `isLexiconPortalAdmin`, etc. */
  isAdmin: boolean;
  /** True only when logged in via staff JWT with admin or scholar role. */
  canAccessAdminRoute: boolean;

  signInContributor: (
    email: string,
    password: string
  ) => Promise<{ error: Error | null; redirectTo?: string }>;

  /** After signup returns JWTs — set tokens in storage first, then call this. */
  hydrateContributorFromStoredTokens: () => Promise<{ error: Error | null }>;

  /** After staff signup returns JWTs — set staff tokens in storage first, then call this. */
  hydrateStaffFromStoredTokens: () => Promise<{
    error: Error | null;
    staffRole: StaffRole | null;
  }>;

  signInStaff: (
    email: string,
    password: string,
    /** Must match the staff login tab — sent as `login_context` to the server. */
    loginContext: StaffRole
  ) => Promise<{
    error: Error | null;
    redirectTo?: "/admin" | "/staff-login";
    staffRole: StaffRole | null;
  }>;

  /** Clears portal JWT only. */
  signOutContributor: () => Promise<void>;
  /** Clears staff JWT only. */
  signOutStaff: () => Promise<void>;

  /** Alias: contributor sign-out (header logout). */
  signOut: () => Promise<void>;
  /** @deprecated Use signInContributor */
  signIn: (
    email: string,
    password: string
  ) => Promise<{ error: Error | null; redirectTo?: "/" | "/admin" }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [contributorUser, setContributorUser] = useState<User | null>(null);
  const [staffUser, setStaffUser] = useState<User | null>(null);
  const [staffRole, setStaffRole] = useState<StaffRole | null>(null);
  const [portalRbac, setPortalRbac] = useState<JainLexiconRbacState | null>(() =>
    getStoredPortalRbac()
  );
  const [staffRbac, setStaffRbac] = useState<JainLexiconRbacState | null>(() =>
    getStoredStaffRbac()
  );
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const bootstrapContributor = async () => {
      const jwt = getContributorAccessToken();
      if (!jwt) {
        setContributorUser(null);
        setStoredPortalRbac(null);
        setPortalRbac(null);
        return;
      }
      const nextUser = decodeUserFromToken(jwt);
      if (!isMounted) {
        return;
      }
      if (!nextUser) {
        setContributorUser(null);
        setContributorTokens(null, null);
        setStoredPortalRbac(null);
        setPortalRbac(null);
        return;
      }
      if (accessTokenIndicatesInactiveAccount(jwt)) {
        setContributorUser(null);
        setContributorTokens(null, null);
        setStoredPortalRbac(null);
        setPortalRbac(null);
        return;
      }
      setContributorUser(nextUser);
      const sync = await syncPortalRbacFromServer();
      if (!isMounted) {
        return;
      }
      setPortalRbac(sync.rbac);
    };

    const bootstrapStaff = async () => {
      const jwt = getStaffAccessToken();
      if (!jwt) {
        setStaffUser(null);
        setStaffRole(null);
        setStoredStaffRbac(null);
        setStaffRbac(null);
        return;
      }
      const u = decodeUserFromToken(jwt);
      const role = decodeStaffRoleFromToken(jwt);
      if (u && role) {
        if (accessTokenIndicatesInactiveAccount(jwt)) {
          setStaffUser(null);
          setStaffRole(null);
          setStaffTokens(null, null);
          setStoredStaffRbac(null);
          setStaffRbac(null);
          return;
        }
        setStaffUser(u);
        setStaffRole(role);
        const sync = await syncStaffRbacFromServer();
        if (!isMounted) {
          return;
        }
        setStaffRbac(sync.rbac);
      } else {
        setStaffUser(null);
        setStaffRole(null);
        setStaffTokens(null, null);
        setStoredStaffRbac(null);
        setStaffRbac(null);
      }
    };

    const run = async () => {
      try {
        await bootstrapStaff();
        await bootstrapContributor();
      } catch (error) {
        console.error("Auth initialization error:", error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void run();

    const timeout = setTimeout(() => {
      if (isMounted && isLoading) {
        console.warn("Auth loading timed out");
        setIsLoading(false);
      }
    }, 8000);

    return () => {
      isMounted = false;
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial bootstrap
  }, []);

  const signInContributor = useCallback(async (email: string, password: string) => {
    const { access, refresh, rbac, error } = await loginPortalPassword(email, password);
    if (error) {
      return { error };
    }
    if (!access) {
      return { error: new Error("Login did not return an access token.") };
    }

    const nextUser = decodeUserFromToken(access);
    if (!nextUser) {
      setContributorTokens(null, null);
      setStoredPortalRbac(null);
      setPortalRbac(null);
      return { error: new Error("Unable to parse user from token.") };
    }

    if (rbac) {
      setStoredPortalRbac(rbac);
    }
    setContributorTokens(access, refresh ?? null);
    setContributorUser(nextUser);

    const sync = await syncPortalRbacFromServer();
    setPortalRbac(sync.rbac);

    return { error: null, redirectTo: "/" };
  }, []);

  const hydrateContributorFromStoredTokens = useCallback(async () => {
    const access = getContributorAccessToken();
    if (!access) {
      return { error: new Error("No access token in storage.") };
    }
    const nextUser = decodeUserFromToken(access);
    if (!nextUser) {
      setContributorTokens(null, null);
      setStoredPortalRbac(null);
      setPortalRbac(null);
      return { error: new Error("Unable to parse user from token.") };
    }
    if (accessTokenIndicatesInactiveAccount(access)) {
      setContributorTokens(null, null);
      setContributorUser(null);
      setStoredPortalRbac(null);
      setPortalRbac(null);
      return { error: new Error(INACTIVE_ACCOUNT_MESSAGE) };
    }
    setContributorUser(nextUser);
    const sync = await syncPortalRbacFromServer();
    setPortalRbac(sync.rbac);
    return { error: null };
  }, []);

  const hydrateStaffFromStoredTokens = useCallback(async () => {
    const access = getStaffAccessToken();
    if (!access) {
      return { error: new Error("No staff access token in storage."), staffRole: null };
    }
    const nextUser = decodeUserFromToken(access);
    const role = decodeStaffRoleFromToken(access);
    if (!nextUser || !role) {
      setStaffTokens(null, null);
      setStoredStaffRbac(null);
      setStaffRbac(null);
      return {
        error: new Error(
          role
            ? "Unable to parse user from staff token."
            : "This account does not have admin or scholar access."
        ),
        staffRole: null,
      };
    }
    if (accessTokenIndicatesInactiveAccount(access)) {
      setStaffTokens(null, null);
      setStaffUser(null);
      setStaffRole(null);
      setStoredStaffRbac(null);
      setStaffRbac(null);
      return { error: new Error(INACTIVE_ACCOUNT_MESSAGE), staffRole: null };
    }
    setStaffUser(nextUser);
    setStaffRole(role);
    const sync = await syncStaffRbacFromServer();
    setStaffRbac(sync.rbac);
    return { error: null, staffRole: role };
  }, []);

  const signInStaff = useCallback(
    async (email: string, password: string, loginContext: StaffRole) => {
      const { access, refresh, staffRole: role, rbac, error } =
        await loginStaffPassword(email, password, loginContext);
      if (error) {
        return { error, redirectTo: "/staff-login" as const, staffRole: null };
      }
      if (!access) {
        return {
          error: new Error("Login did not return an access token."),
          redirectTo: "/staff-login" as const,
          staffRole: null,
        };
      }

      const nextUser = decodeUserFromToken(access);
      if (!nextUser || !role) {
        setStaffTokens(null, null);
        setStoredStaffRbac(null);
        setStaffRbac(null);
        return {
          error: new Error(
            role
              ? "Unable to parse user from token."
              : "This account does not have admin or scholar access."
          ),
          redirectTo: "/staff-login" as const,
          staffRole: null,
        };
      }

      if (rbac) {
        setStoredStaffRbac(rbac);
      }
      setStaffTokens(access, refresh ?? null);
      setStaffUser(nextUser);
      setStaffRole(role);

      const sync = await syncStaffRbacFromServer();
      setStaffRbac(sync.rbac);

      return { error: null, redirectTo: "/admin" as const, staffRole: role };
    },
    []
  );

  const signOutContributor = useCallback(async () => {
    await logoutContributorRemote(getContributorRefreshToken());
    setContributorTokens(null, null);
    setContributorUser(null);
    setStoredPortalRbac(null);
    setPortalRbac(null);
  }, []);

  const signOutStaff = useCallback(async () => {
    await logoutStaffRemote(getStaffRefreshToken());
    setStaffTokens(null, null);
    setStaffUser(null);
    setStaffRole(null);
    setStoredStaffRbac(null);
    setStaffRbac(null);
  }, []);

  const canAccessStaffDashboard =
    !!(staffUser && staffRole && (staffRole === "admin" || staffRole === "scholar"));

  const canAccessAdminRoute = canAccessStaffDashboard;

  return (
    <AuthContext.Provider
      value={{
        contributorSession: contributorUser ? { user: contributorUser } : null,
        contributorUser,
        isContributorAuthenticated: !!contributorUser,
        portalRbac,
        staffSession: staffUser ? { user: staffUser } : null,
        staffUser,
        staffRole,
        isStaffAuthenticated: !!staffUser && !!staffRole,
        staffRbac,
        canAccessStaffDashboard,

        hasPortalPermission: (permissionId: number) =>
          rbacHasPermission(portalRbac, permissionId),
        hasStaffPermission: (permissionId: number) =>
          rbacHasPermission(staffRbac, permissionId),
        hasPortalRole: (role: AppLexiconRole) => rbacHasRole(portalRbac, role),
        hasStaffRole: (role: AppLexiconRole) => rbacHasRole(staffRbac, role),
        isLexiconPortalAdmin: () => rbacIsLexiconAdmin(portalRbac),
        isLexiconPortalScholar: () => rbacIsScholar(portalRbac),
        isLexiconPortalContributor: () => rbacIsContributor(portalRbac),
        isLexiconStaffAdmin: () => rbacIsLexiconAdmin(staffRbac),
        isLexiconStaffScholar: () => rbacIsScholar(staffRbac),

        user: contributorUser,
        isLoading,
        isAdmin:
          rbacIsLexiconAdmin(portalRbac) || staffRole === "admin",
        canAccessAdminRoute,

        signInContributor,
        hydrateContributorFromStoredTokens,
        hydrateStaffFromStoredTokens,
        signInStaff,
        signOutContributor,
        signOutStaff,
        signOut: signOutContributor,
        signIn: async (email, password) => {
          const r = await signInContributor(email, password);
          const dest =
            r.redirectTo === "/" ? "/" : r.redirectTo === "/admin" ? "/admin" : "/";
          return {
            error: r.error,
            ...(r.redirectTo ? { redirectTo: dest as "/" | "/admin" } : {}),
          };
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
